<template>
  <!-- Where a message is written: a plain line that grows with what is
       typed, its tools on the right — emoji, files (the paperclip, a paste,
       or a drop on the conversation), and a voice message (the microphone,
       while there is nothing typed; the send arrow takes its place once there
       is). Enter sends on a computer; on a phone Enter is a new line and the
       arrow sends. Editing a message happens here too, and a reply shows what
       it replies to above the line (the ✕ or Escape lets it go). In the apps'
       popup layout the emoji come up as a compact grid pointing at the
       smiley. -->
  <form
    class="sc-composer"
    :class="{ 'sc-composer--popup': popup, 'is-editing': editing, 'is-recording': recording, 'is-disabled': disabled }"
    @submit.prevent="submit"
  >
    <div v-if="replying && !editing" class="sc-composer__reply">
      <i class="pi pi-reply sc-composer__reply-icon" aria-hidden="true"></i>
      <chat-quote :quote="replying.quote" :author-label="replying.label" :clickable="false" />
      <button type="button" class="sc-composer__context-close" aria-label="Cancel the reply" title="Cancel (Esc)" @click="$emit('cancel-reply')">
        <i class="pi pi-times" aria-hidden="true"></i>
      </button>
    </div>

    <div v-if="editing" class="sc-composer__context">
      <i class="pi pi-pencil" aria-hidden="true"></i>
      <span class="sc-composer__context-text">
        <strong>Editing message</strong>
        <span>{{ editingPreview }}</span>
      </span>
      <button type="button" class="sc-composer__context-close" aria-label="Cancel editing" title="Cancel (Esc)" @click="$emit('cancel-edit')">
        <i class="pi pi-times" aria-hidden="true"></i>
      </button>
    </div>

    <ul v-if="files.length && !editing" class="sc-chips" aria-label="Files to send">
      <li v-for="(file, index) in files" :key="fileKey(file)" class="sc-chip" :title="file.name">
        <span class="sc-chip__thumb">
          <img v-if="previews[fileKey(file)]" :src="previews[fileKey(file)]" alt="" />
          <i v-else class="pi" :class="chatFileIcon(file)" aria-hidden="true"></i>
        </span>
        <span class="sc-chip__meta">
          <span class="sc-chip__name">{{ file.name }}</span>
          <span class="sc-chip__size">{{ formatFileSize(file.size) }}</span>
        </span>
        <button type="button" class="sc-chip__remove" :aria-label="`Remove ${file.name}`" @click="removeFile(index)">
          <i class="pi pi-times" aria-hidden="true"></i>
        </button>
      </li>
    </ul>

    <chat-voice-recorder v-if="recording" @send="onVoice" @cancel="recording = false" />
    <div v-else class="sc-composer__row">
      <div class="sc-composer__tools">
        <button
          ref="emojiButton"
          type="button"
          class="sc-tool"
          :class="{ 'is-active': showEmoji }"
          :disabled="disabled"
          title="Emoji"
          aria-label="Add an emoji"
          :aria-expanded="showEmoji ? 'true' : 'false'"
          @click="toggleEmoji"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 15c.91 1.21 2.36 2 4 2s3.09-.79 4-2" />
            <path d="M8.01 9H8m8 0h-.01" stroke-width="2" />
          </svg>
        </button>
        <button
          v-if="!editing"
          type="button"
          class="sc-tool"
          :disabled="disabled || files.length >= maxFiles"
          title="Attach files"
          aria-label="Attach files"
          @click="pickFiles"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M19.25 9.5v4.25a7.25 7.25 0 0 1-14.5 0V7a4.5 4.5 0 0 1 9 0v6a2.25 2.25 0 0 1-4.5 0V8.5" />
          </svg>
        </button>
        <chat-emoji-picker
          v-if="showEmoji"
          :compact="popup"
          :style="popup && emojiTail ? { '--sc-emoji-tail': `${emojiTail}px` } : null"
          @pick="insertText"
          @close="showEmoji = false"
        />
      </div>
      <textarea
        ref="input"
        v-model="draft"
        class="sc-composer__input"
        rows="1"
        :placeholder="editing ? 'Edit your message…' : replying ? 'Write a reply…' : placeholder"
        :disabled="disabled"
        :aria-label="editing ? 'Edit your message' : 'Message'"
        enterkeyhint="send"
        @keydown="onKeydown"
        @paste="onPaste"
        @input="onInput"
      ></textarea>
      <button
        v-if="showSend"
        type="submit"
        class="sc-send"
        :disabled="!canSubmit"
        :title="editing ? 'Save' : 'Send'"
        :aria-label="editing ? 'Save the change' : 'Send message'"
      >
        <svg v-if="editing" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
        <svg v-else viewBox="0 0 24 24" aria-hidden="true">
          <path d="M21.05 3.05c-2.18-2.35-18.56 3.4-18.55 5.5.02 2.38 6.4 3.11 8.17 3.61 1.06.3 1.35.6 1.6 1.72 1.1 5.05 1.66 7.57 2.93 7.62 2.03.09 7.97-16.16 5.85-18.45Z" />
          <path d="M11.5 12.5L15 9" />
        </svg>
      </button>
      <button
        v-else
        type="button"
        class="sc-send sc-send--mic"
        :disabled="disabled"
        title="Record a voice message"
        aria-label="Record a voice message"
        @click="recording = true"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="8.5" y="2.25" width="7" height="12.75" rx="3.5" />
          <path d="M5 11.5a7 7 0 0 0 14 0M12 18.5v3.25" />
        </svg>
      </button>
    </div>

    <div v-if="!recording && (hintText || nearLimit)" class="sc-composer__meta">
      <span class="sc-composer__hint">{{ hintText }}</span>
      <span v-if="nearLimit" class="sc-composer__count" :class="{ 'is-over': tooLong }">
        {{ draft.length.toLocaleString() }} / {{ maxLength.toLocaleString() }}
      </span>
    </div>

    <input
      ref="fileInput"
      type="file"
      class="sc-hidden-input"
      multiple
      :accept="accept"
      tabindex="-1"
      aria-hidden="true"
      @change="onFileInput"
    />
  </form>
</template>
<script>
import chatEmojiPicker from '@/components/chat/Chat_Emoji_Picker.vue';
import chatQuote from '@/components/chat/Chat_Quote.vue';
import chatVoiceRecorder from '@/components/chat/Chat_Voice_Recorder.vue';
import {
  acceptChatFiles,
  canRecordVoice,
  chatDrafts,
  chatFileIcon,
  CHAT_FILE_ACCEPT,
  CHAT_FILE_MAX_FILES,
  CHAT_MESSAGE_MAX_LENGTH,
  formatFileSize,
  isChatImage,
} from '@/utils/chat';
import { TOAST_GLOBAL_CONFIG } from '@/utils/const';

const INPUT_MAX_HEIGHT_PX = 160;
// Typing is announced at most this often.
const TYPING_EVERY_MS = 2500;

export default {
  name: 'ChatComposer',
  components: {
    chatEmojiPicker,
    chatQuote,
    chatVoiceRecorder,
  },
  props: {
    placeholder: {
      type: String,
      default: 'Write a message…',
    },
    // Where what is typed is kept between visits (utils/chat chatDrafts).
    draftKey: {
      type: String,
      default: '',
    },
    // The message being edited, or null.
    editing: {
      type: Object,
      default: null,
    },
    // What the next message replies to: { quote, label } (components/chat/Chat_Thread).
    replying: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // Shows "Enter to send…" under the box on a computer.
    showHint: {
      type: Boolean,
      default: true,
    },
    // 'default' (the admin), or 'popup' (the apps' chat).
    layout: {
      type: String,
      default: 'default',
    },
  },
  emits: ['send', 'save-edit', 'cancel-edit', 'cancel-reply', 'typing'],
  data() {
    return {
      draft: this.draftKey ? chatDrafts.get(this.draftKey) : '',
      files: [],
      previews: {},
      recording: false,
      showEmoji: false,
      // How far the smiley's middle is from the composer's right edge: where
      // the popup picker's pointer goes.
      emojiTail: 0,
      accept: CHAT_FILE_ACCEPT,
      maxFiles: CHAT_FILE_MAX_FILES,
      maxLength: CHAT_MESSAGE_MAX_LENGTH,
      canRecord: canRecordVoice(),
      // On a touch keyboard Enter makes a new line; the arrow sends.
      isTouch: typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches,
    };
  },
  computed: {
    popup() {
      return this.layout === 'popup';
    },
    body() {
      return this.draft.trim();
    },
    tooLong() {
      return this.draft.length > this.maxLength;
    },
    nearLimit() {
      return this.draft.length > this.maxLength - 500;
    },
    editingHasFiles() {
      return Boolean(this.editing && Array.isArray(this.editing.attachments) && this.editing.attachments.length);
    },
    canSubmit() {
      if (this.disabled || this.tooLong) return false;
      if (this.editing) return this.body.length > 0 || this.editingHasFiles;
      return this.body.length > 0 || this.files.length > 0;
    },
    // The arrow while there is something to send; the microphone otherwise.
    showSend() {
      return Boolean(this.editing) || this.body.length > 0 || this.files.length > 0 || !this.canRecord;
    },
    editingPreview() {
      const text = String(this.editing?.body || '').replace(/\s+/g, ' ').trim();
      return text.length > 80 ? `${text.slice(0, 79)}…` : text;
    },
    hintText() {
      if (!this.showHint || this.isTouch) return '';
      if (this.editing) return 'Enter to save · Esc to cancel';
      return 'Enter to send · Shift + Enter for a new line';
    },
  },
  watch: {
    // Starting a reply puts the cursor in the box (on a phone too, so the
    // keyboard comes up for it).
    replying(reply, previous) {
      if (reply && (!previous || reply.quote.id !== previous.quote.id)) this.$nextTick(() => this.focus(true));
    },
    // Editing takes the box over; what was being written comes back after.
    editing(message, previous) {
      if (message && !previous) this.savedDraft = this.draft;
      if (message) {
        this.draft = String(message.body || '');
        this.showEmoji = false;
        this.$nextTick(() => {
          this.autogrow();
          this.focus(true);
        });
      } else if (previous) {
        this.draft = this.savedDraft || '';
        this.savedDraft = '';
        this.$nextTick(() => this.autogrow());
      }
    },
    draft(value) {
      if (!this.editing && this.draftKey) chatDrafts.set(this.draftKey, value);
    },
    files(list) {
      this.syncPreviews(list);
    },
  },
  methods: {
    chatFileIcon,
    formatFileSize,
    fileKey(file) {
      return `${file.name}:${file.size}:${file.lastModified}`;
    },
    toggleEmoji() {
      this.showEmoji = !this.showEmoji;
      if (this.showEmoji && this.popup) this.placeEmojiTail();
    },
    // In the composer's own pixels — the smiley's offset inside it — which a
    // zoomed page (the apps' html.app-zoomed) does not scale, as it does the
    // on-screen boxes.
    placeEmojiTail() {
      const button = this.$refs.emojiButton;
      if (!button || !this.$el || button.offsetParent !== this.$el) return;
      this.emojiTail = Math.round(this.$el.clientWidth - (button.offsetLeft + button.offsetWidth / 2));
    },
    /** Puts the cursor in the box — on a phone only when asked, so the keyboard does not jump up. */
    focus(evenOnTouch = false) {
      if (this.isTouch && !evenOnTouch) return;
      const input = this.$refs.input;
      if (!input) return;
      input.focus();
      const end = input.value.length;
      try {
        input.setSelectionRange(end, end);
      } catch (error) {
        // Not a text field any more.
      }
    },
    /** Puts text (an emoji, a suggestion) where the cursor is. */
    insertText(text) {
      const input = this.$refs.input;
      if (!input) {
        this.draft += text;
        return;
      }
      const start = input.selectionStart ?? this.draft.length;
      const end = input.selectionEnd ?? this.draft.length;
      this.draft = `${this.draft.slice(0, start)}${text}${this.draft.slice(end)}`;
      this.$nextTick(() => {
        input.focus();
        const caret = start + text.length;
        input.setSelectionRange(caret, caret);
        this.autogrow();
      });
    },
    /** Adds what was picked, dropped or pasted; says why anything was left out. */
    addFiles(list) {
      if (this.disabled || this.editing || !list || !list.length) return;
      const { files, errors } = acceptChatFiles(this.files, list);
      errors.forEach((message) => this.$toast.error(message, TOAST_GLOBAL_CONFIG));
      if (files.length !== this.files.length) this.files = files;
    },
    removeFile(index) {
      this.files = this.files.filter((_, position) => position !== index);
    },
    pickFiles() {
      this.$refs.fileInput?.click();
    },
    onFileInput(event) {
      this.addFiles(event.target.files);
      // The same file picked again still fires a change.
      event.target.value = '';
    },
    onPaste(event) {
      const files = event.clipboardData?.files;
      if (files && files.length && !this.editing) {
        event.preventDefault();
        this.addFiles(files);
      }
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        if (this.showEmoji) {
          this.showEmoji = false;
          event.preventDefault();
        } else if (this.editing) {
          this.$emit('cancel-edit');
          event.preventDefault();
        } else if (this.replying) {
          this.$emit('cancel-reply');
          event.preventDefault();
        }
        return;
      }
      if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && !this.isTouch) {
        event.preventDefault();
        this.submit();
      }
    },
    onInput() {
      this.autogrow();
      if (this.editing || !this.body) return;
      const now = Date.now();
      if (now - this.typingAt >= TYPING_EVERY_MS) {
        this.typingAt = now;
        this.$emit('typing');
      }
    },
    // The box grows with what is typed, up to INPUT_MAX_HEIGHT_PX, then
    // scrolls. Empty, it is one line (a long placeholder would count).
    autogrow() {
      const input = this.$refs.input;
      if (!input) return;
      input.style.height = '';
      if (!input.value) {
        input.style.overflowY = 'hidden';
        return;
      }
      const borders = input.offsetHeight - input.clientHeight;
      const wanted = input.scrollHeight + borders;
      input.style.height = `${Math.min(wanted, INPUT_MAX_HEIGHT_PX)}px`;
      input.style.overflowY = wanted > INPUT_MAX_HEIGHT_PX ? 'auto' : 'hidden';
    },
    submit() {
      if (!this.canSubmit) {
        if (this.tooLong) this.$toast.error(`A message can be up to ${this.maxLength.toLocaleString()} characters long.`, TOAST_GLOBAL_CONFIG);
        return;
      }
      if (this.editing) {
        this.$emit('save-edit', this.body);
        return;
      }
      const message = { body: this.body, files: [...this.files], voice: null };
      // Sent, so no draft any more — under the conversation it was typed in,
      // even when sending moves the composer to another (a closed chat's
      // message starts a new one).
      if (this.draftKey) chatDrafts.set(this.draftKey, '');
      this.$emit('send', message);
      this.draft = '';
      this.files = [];
      this.showEmoji = false;
      this.typingAt = 0;
      this.$nextTick(() => {
        this.autogrow();
        this.focus();
      });
    },
    onVoice(voice) {
      this.recording = false;
      this.$emit('send', { body: '', files: [], voice });
      this.$nextTick(() => this.focus());
    },
    // Thumbnails of the picked pictures: made once each, let go as soon as they are gone.
    syncPreviews(files) {
      const keep = new Set(files.map((file) => this.fileKey(file)));
      const previews = { ...this.previews };
      Object.keys(previews).forEach((key) => {
        if (keep.has(key)) return;
        URL.revokeObjectURL(previews[key]);
        delete previews[key];
      });
      files.forEach((file) => {
        const key = this.fileKey(file);
        if (!previews[key] && isChatImage(file.name, file.type)) previews[key] = URL.createObjectURL(file);
      });
      this.previews = previews;
    },
  },
  created() {
    this.savedDraft = '';
    this.typingAt = 0;
  },
  mounted() {
    if (this.draft) this.$nextTick(() => this.autogrow());
  },
  beforeUnmount() {
    Object.values(this.previews).forEach((url) => URL.revokeObjectURL(url));
  },
};
</script>
